import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { CreateProjectInput } from '@/modules/common/types';

interface CreateProjectDialogProps {
  open: boolean;
  createLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (input: CreateProjectInput) => void;
}

export const CreateProjectDialog = ({
  open: controlledOpen,
  createLoading,
  onOpenChange: controlledOnOpenChange,
  onCreate: createProject,
}: CreateProjectDialogProps) => {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange || setInternalOpen;

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setName('');
      setDescription('');
    }
  };

  const validateForm = () => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push(t('modules.mavue2.project.validation.projectNameRequired'));
    }

    if (name.trim().length < 2) {
      errors.push(t('modules.mavue2.project.validation.projectNameMin'));
    }

    if (name.trim().length > 100) {
      errors.push(t('modules.mavue2.project.validation.projectNameMax'));
    }

    if (description.length > 500) {
      errors.push(t('modules.mavue2.project.validation.descriptionMax'));
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (errors.length > 0) {
      toast.error(t('modules.mavue2.common.validationError'), {
        description: errors[0],
      });
      return;
    }

    try {
      createProject({
        title: name.trim(),
        description: description.trim() || undefined,
      });

      // Close dialog and reset form on success
      handleOpenChange(false);
    } catch (error) {
      // Error handling is done in the hook
      console.error('Failed to create project:', error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('modules.mavue2.project.create.title')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="project-name">
              {t('modules.mavue2.project.create.projectName')}{' '}
              <span className="text-red-500">*</span>
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('modules.mavue2.project.create.projectNamePlaceholder')}
              className="w-full"
              disabled={createLoading}
              autoFocus
            />
            <p className="text-xs text-gray-500">
              {t('modules.mavue2.project.create.projectNameHelp')}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="project-description">
              {t('modules.mavue2.project.create.descriptionOptional')}
            </Label>
            <Textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('modules.mavue2.project.create.descriptionPlaceholder')}
              className="w-full"
              rows={3}
              disabled={createLoading}
            />
            <p className="text-xs text-gray-500">
              {t('modules.mavue2.project.create.descriptionHelp')}
            </p>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createLoading}
            >
              {t('modules.mavue2.common.cancel')}
            </Button>
            <Button type="submit" disabled={createLoading || !name.trim()}>
              {createLoading ? (
                <>
                  <Spinner />
                </>
              ) : (
                t('modules.mavue2.project.create.submit')
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
